import React, { useState, useEffect } from "react";
import { Clock } from "lucide-react";

interface TimeInputProps {
    value: string; // 24-hour format "HH:mm"
    onChange: (value: string) => void;
    disabled?: boolean;
    use24Hour?: boolean;
    className?: string;
}

const TimeInput: React.FC<TimeInputProps> = ({
    value,
    onChange,
    disabled = false,
    use24Hour = false,
    className = ""
}) => {
    const [hour, setHour] = useState<string>("09");
    const [minute, setMinute] = useState<string>("00");
    const [period, setPeriod] = useState<"AM" | "PM">("AM");

    // Generate hour options based on format
    const hourOptions = use24Hour
        ? Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"))
        : Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, "0"));

    // Generate minute options (00, 15, 30, 45)
    const minuteOptions = ["00", "15", "30", "45"];

    // Parse the 24-hour value into hour, minute, and period
    useEffect(() => {
        if (value) {
            const [hourStr, minuteStr] = value.split(":");
            const hour24 = parseInt(hourStr, 10);

            if (use24Hour) {
                setHour(hourStr);
                setMinute(minuteStr);
            } else {
                // Convert to 12-hour format
                const newPeriod = hour24 >= 12 ? "PM" : "AM";
                const hour12 = hour24 % 12 || 12;
                setPeriod(newPeriod);
                setHour(hour12.toString().padStart(2, "0"));
                setMinute(minuteStr);
            }
        }
    }, [value, use24Hour]);

    // Convert back to 24-hour format and call onChange
    const updateValue = (newHour: string, newMinute: string, newPeriod: "AM" | "PM") => {
        if (!newHour || !newMinute) return;

        let hour24: number;
        
        if (use24Hour) {
            hour24 = parseInt(newHour, 10);
        } else {
            const hour12 = parseInt(newHour, 10);
            if (newPeriod === "PM" && hour12 !== 12) {
                hour24 = hour12 + 12;
            } else if (newPeriod === "AM" && hour12 === 12) {
                hour24 = 0;
            } else {
                hour24 = hour12;
            }
        }

        const formattedHour = hour24.toString().padStart(2, "0");
        const formattedMinute = newMinute.padStart(2, "0");
        onChange(`${formattedHour}:${formattedMinute}`);
    };

    const handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newHour = e.target.value;
        setHour(newHour);
        updateValue(newHour, minute, period);
    };

    const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newMinute = e.target.value;
        setMinute(newMinute);
        updateValue(hour, newMinute, period);
    };

    const handlePeriodChange = (newPeriod: "AM" | "PM") => {
        setPeriod(newPeriod);
        updateValue(hour, minute, newPeriod);
    };

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <div className="relative flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                
                {/* Hour Select */}
                <select
                    value={hour}
                    onChange={handleHourChange}
                    disabled={disabled}
                    className="text-center border-none outline-none bg-transparent disabled:text-gray-400 cursor-pointer font-medium text-gray-700 hover:text-blue-600 transition-colors"
                    style={{ width: "45px" }}
                >
                    {hourOptions.map((h) => (
                        <option key={h} value={h}>
                            {h}
                        </option>
                    ))}
                </select>

                <span className="text-gray-400 font-medium">:</span>

                {/* Minute Select */}
                <select
                    value={minute}
                    onChange={handleMinuteChange}
                    disabled={disabled}
                    className="text-center border-none outline-none bg-transparent disabled:text-gray-400 cursor-pointer font-medium text-gray-700 hover:text-blue-600 transition-colors"
                    style={{ width: "45px" }}
                >
                    {minuteOptions.map((m) => (
                        <option key={m} value={m}>
                            {m}
                        </option>
                    ))}
                </select>

                {/* AM/PM Toggle for 12-hour format */}
                {!use24Hour && (
                    <div className="flex ml-2 border-l pl-2 gap-1">
                        <button
                            type="button"
                            onClick={() => handlePeriodChange("AM")}
                            disabled={disabled}
                            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                                period === "AM"
                                    ? "bg-blue-500 text-white shadow-sm"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            AM
                        </button>
                        <button
                            type="button"
                            onClick={() => handlePeriodChange("PM")}
                            disabled={disabled}
                            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                                period === "PM"
                                    ? "bg-blue-500 text-white shadow-sm"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            PM
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TimeInput;

